import { FC } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Interface/FormFields/Checkbox/Checkbox";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { TableSelectAllCheckbox } from "@/components/Layout/Table/TableSelectAllCheckbox/TableSelectAllCheckbox";
import { BillingAccountChip } from "@/components/Portal/Documents/BillingAccounts/BillingAccountChip";
import {
  BillingAccountSubscription,
  getBillingAccountDisplayValue,
  getMovableSimIds,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { DrAccount } from "@/services/model";

import { BulkMoveFormValues } from "./bulkMoveFormValues";

interface Props {
  source: DrAccount;
  /** Only the Abos that can be moved, i.e. those with a SIM id. */
  subscriptions: BillingAccountSubscription[];
  selectedSimIds: string[];
  control: Control<BulkMoveFormValues>;
  register: UseFormRegister<BulkMoveFormValues>;
  errors: FieldErrors<BulkMoveFormValues>;
  onSelectionChange: (simIds: string[]) => void;
}

/** "1. Abos wählen": the source Vertragskonto and its Abos to move. */
export const SubscriptionSelection: FC<Props> = ({
  source,
  subscriptions,
  selectedSimIds,
  control,
  register,
  errors,
  onSelectionChange,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.bulkMove",
  });
  const simIds = getMovableSimIds(subscriptions);

  return (
    <ProfileCard
      dataTestid="bulk-move-selection"
      variant="white"
      title={t("selectHeadline")}
    >
      <Controller
        control={control}
        name="source"
        render={({ field }) => (
          <Combobox<DrAccount>
            dataTestId="bulk-move-source"
            field={field}
            label={t("source")}
            displayValue={getBillingAccountDisplayValue}
            options={[source]}
            placeholder={t("source")}
            disabled
          />
        )}
      />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 py-2 pr-1">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <p className="truncate text-base font-semibold leading-md text-foreground-primary">
              {t("selected")}
            </p>
            <BillingAccountChip text={selectedSimIds.length} />
          </div>
          <div className="flex w-10 shrink-0 justify-center">
            <TableSelectAllCheckbox
              dataTestid="bulk-move-select-all"
              currentPageIds={simIds}
              selectedIds={selectedSimIds}
              labels={{
                selectAll: t("selectAll"),
                deselectAll: t("deselectAll"),
              }}
              onChange={onSelectionChange}
            />
          </div>
        </div>
        <ul className="flex flex-col gap-2">
          {subscriptions.map((subscription, index) => {
            const checkboxId = `bulk-move-subscription-${index}`;

            return (
              <li
                key={`${subscription.simId}-${index}`}
                className="flex h-16 items-center gap-2 rounded-md bg-surface-quaternary p-1"
              >
                <label
                  className="min-w-0 flex-1 cursor-pointer truncate px-3 text-base font-semibold leading-md text-foreground-primary"
                  htmlFor={checkboxId}
                >
                  {subscription.label}
                </label>
                <div className="flex w-10 shrink-0 justify-center">
                  <Checkbox<BulkMoveFormValues>
                    id={checkboxId}
                    name="simIds"
                    value={subscription.simId}
                    options={{}}
                    errors={errors}
                    register={register}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </ProfileCard>
  );
};
